<template>
  <div class="p-1">
    <h6 class="mb-1">
      Select Orders
    </h6>
    <p class="text-muted mb-2">
      Orders containing your selected products that are eligible for shipment.
    </p>

    <div
      v-if="isLoading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>

    <template v-else>
      <div
        v-if="orders.length === 0"
        class="text-center py-3"
      >
        <p class="text-muted">
          No eligible orders found for the selected products.
        </p>
      </div>

      <template v-else>
        <div class="mb-1">
          <div
            class="d-flex align-items-center"
            style="gap: 1%"
          >
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by retailer or order IDs (comma-separated)..."
              style="flex: 0 0 61%"
            />
            <v-select
              v-model="selectedRetailers"
              :options="uniqueRetailers"
              multiple
              placeholder="Select retailer..."
              label="label"
              :reduce="opt => opt.value"
              style="flex: 0 0 38%"
            />
          </div>
        </div>

        <div class="mb-1 d-flex justify-content-between align-items-center">
          <b-form-checkbox
            :checked="allFilteredSelected"
            :indeterminate="someFilteredSelected && !allFilteredSelected"
            @change="toggleAll"
          >
            Select All ({{ filteredOrders.length }})
          </b-form-checkbox>
          <small class="text-muted font-weight-bold">
            {{ localSelectedIds.length }} of {{ orders.length }} orders selected
          </small>
        </div>

        <div
          class="order-list scroll-bar"
          style="max-height: 350px; overflow-y: auto;"
        >
          <b-table
            :items="filteredOrders"
            :fields="tableFields"
            responsive
            small
            hover
            class="mb-0"
            @row-clicked="toggleOrder"
          >
            <template #cell(selected)="data">
              <b-form-checkbox
                :checked="localSelectedIds.includes(data.item._id)"
                @change="toggleOrder(data.item)"
                @click.native.stop
              />
            </template>
            <template #cell(orderSeqId)="data">
              {{ data.item.orderSeqId }}
            </template>
            <template #cell(retailerName)="data">
              {{ data.item.retailerName }}
            </template>
            <template #cell(eligibleProductCount)="data">
              {{ data.item.eligibleProductCount }} / {{ data.item.totalProductCount }}
            </template>
          </b-table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BSpinner,
  BFormInput,
  BFormCheckbox,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { GET_BULK_SHIP_ELIGIBLE_ORDERS } from '@/store/modules/order.module'

export default {
  components: {
    BSpinner,
    BFormInput,
    BFormCheckbox,
    BTable,
    vSelect,
  },
  props: {
    collectionId: {
      type: String,
      required: true,
    },
    productIds: {
      type: Array,
      required: true,
    },
    selectedOrderIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      orders: [],
      localSelectedIds: [...this.selectedOrderIds],
      searchQuery: '',
      selectedRetailers: [],
      tableFields: [
        { key: 'selected', label: '', thStyle: { width: '40px' } },
        { key: 'orderSeqId', label: 'Order ID', sortable: true },
        { key: 'retailerName', label: 'Retailer', sortable: true },
        { key: 'eligibleProductCount', label: 'Eligible Products' },
      ],
    }
  },
  computed: {
    uniqueRetailers() {
      const seen = new Map()
      this.orders.forEach(o => {
        if (o.retailerName && !seen.has(o.retailerName)) {
          seen.set(o.retailerName, {
            label: o.retailerEmail
              ? `${o.retailerName} (${o.retailerEmail})`
              : o.retailerName,
            value: o.retailerName,
          })
        }
      })
      return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label))
    },
    filteredOrders() {
      let filtered = this.orders
      if (this.selectedRetailers.length > 0) {
        filtered = filtered.filter(o => this.selectedRetailers.includes(o.retailerName))
      }
      if (this.searchQuery) {
        const terms = this.searchQuery.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
        if (terms.length > 0) {
          filtered = filtered.filter(o => terms.some(q => (o.retailerName || '').toLowerCase().includes(q)
              || (o.orderSeqId || '').toLowerCase().includes(q)))
        }
      }
      return filtered
    },
    allFilteredSelected() {
      return this.filteredOrders.length > 0
        && this.filteredOrders.every(o => this.localSelectedIds.includes(o._id))
    },
    someFilteredSelected() {
      return this.filteredOrders.some(o => this.localSelectedIds.includes(o._id))
    },
  },
  watch: {
    productIds: {
      immediate: true,
      handler() {
        this.loadOrders()
      },
    },
  },
  methods: {
    async loadOrders() {
      if (!this.collectionId || !this.productIds.length) return
      this.isLoading = true
      try {
        const res = await store.dispatch(GET_BULK_SHIP_ELIGIBLE_ORDERS, {
          collectionId: this.collectionId,
          productIds: this.productIds,
        })
        this.orders = res?.data?.data?.orders || []
        // Auto-select all orders on load
        if (this.localSelectedIds.length === 0) {
          this.localSelectedIds = this.orders.map(o => o._id)
          this.$emit('update', [...this.localSelectedIds])
        }
      } catch {
        this.orders = []
      } finally {
        this.isLoading = false
      }
    },
    toggleOrder(item) {
      const idx = this.localSelectedIds.indexOf(item._id)
      if (idx >= 0) {
        this.localSelectedIds.splice(idx, 1)
      } else {
        this.localSelectedIds.push(item._id)
      }
      this.$emit('update', [...this.localSelectedIds])
    },
    toggleAll(checked) {
      if (checked) {
        const filteredIds = this.filteredOrders.map(o => o._id)
        const merged = new Set([...this.localSelectedIds, ...filteredIds])
        this.localSelectedIds = [...merged]
      } else {
        const filteredIds = new Set(this.filteredOrders.map(o => o._id))
        this.localSelectedIds = this.localSelectedIds.filter(id => !filteredIds.has(id))
      }
      this.$emit('update', [...this.localSelectedIds])
    },
  },
}
</script>

<style lang="scss" scoped>
.v-select {
  ::v-deep .vs__dropdown-toggle {
    min-height: 38px;
    padding: 0 0 0 0;
  }
}
</style>
